import React from "react";
import { siteConfig } from "../../config/siteConfig";
import Icon from "../ui/Icon";
import SealChip from "../ui/SealChip";
import Skeleton from "../ui/Skeleton";
import useInView from "../../hooks/useInView";

const DemoExamSection = ({ onDemoClick }) => {
  const { demo, sealLabels } = siteConfig;

  // Mockup preview is driven by real demo data; first question seeds the plate.
  const questions = Array.isArray(demo.questions) ? demo.questions : [];
  const sample = questions[0] || null;

  // Seal label is config-driven with a safe fallback (never blank, never hardcoded brand).
  const simulacroLabel = (sealLabels && sealLabels.official) || "Oficial";

  // Scroll-in drives the progress fill + one-shot correct-answer reveal.
  const { ref: mockupRef, inView } = useInView({ threshold: 0.3 });

  return (
    <section id="demo" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-slate-900 rounded-2xl p-8 md:p-16 overflow-hidden elev-3">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-teal-600/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-[100px]" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-teal-400 mb-6">
                <span className="h-px w-6 bg-teal-400/50" aria-hidden="true" />
                {demo.sectionEyebrow}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                {demo.sectionTitlePart1} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">
                  {demo.sectionTitlePart2}
                </span>
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-lg leading-relaxed text-pretty">
                {demo.sectionSubtitle}
              </p>

              <div className="mb-10">
                <SealChip label={simulacroLabel} icon="seal" tone="teal" />
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                {demo.bullets.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {item.iconKey ? (
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-500/10 text-teal-400 ring-1 ring-inset ring-teal-500/20">
                        <Icon name={item.iconKey} className="w-5 h-5" />
                      </span>
                    ) : item.icon ? (
                      <span className="text-xl" aria-hidden="true">{item.icon}</span>
                    ) : null}
                    <h4 className="text-white font-semibold text-sm">{item.label}</h4>
                  </div>
                ))}
              </div>

              <button
                onClick={onDemoClick}
                className="group relative inline-flex items-center gap-4 px-10 py-5 bg-white text-slate-900 text-xl font-bold rounded-2xl aurora-ring transition-all duration-200 ease-out hover:-translate-y-0.5 active:scale-[.98] elev-2"
              >
                <span className="bg-teal-700 text-white p-1.5 rounded-full transition-transform duration-200 group-hover:translate-x-0.5">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  </svg>
                </span>
                <span>{demo.ctaLabel}</span>
                <span className="px-3 py-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">
                  {demo.ctaBadge}
                </span>
              </button>
            </div>

            <div
              ref={mockupRef}
              className="relative hidden lg:block"
              aria-hidden="true"
            >
              <div className="card-plate bg-slate-800 border-slate-700 p-2">
                <div className="bg-slate-900 rounded-t-xl p-4 border-b border-slate-700 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-teal-500/30 bg-teal-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-teal-300">
                    <Icon name="seal" className="w-3.5 h-3.5" />
                    {simulacroLabel}
                  </span>
                  <div className="text-teal-400 font-bold text-xs font-mono tabular-nums">45:12</div>
                </div>

                {sample ? (
                  <div className="p-8 space-y-4">
                    {/* Progress fill — animates from a baseline to ~65% on scroll-in. */}
                    <div className="h-1.5 w-full rounded-full bg-slate-700 overflow-hidden mb-6">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-[width] duration-1000 ease-out"
                        style={{ width: inView ? "65%" : "12%" }}
                      />
                    </div>

                    <div className="h-3 w-3/4 bg-slate-700 rounded-full" />
                    <div className="h-3 w-1/2 bg-slate-700 rounded-full mb-8" />

                    {sample.o.slice(0, 3).map((_, o) => {
                      const isCorrect = o === sample.c;
                      const revealed = inView && isCorrect;
                      return (
                        <div
                          key={o}
                          className={`h-10 w-full rounded-xl border flex items-center px-4 gap-3 transition-colors duration-500 ease-out ${
                            revealed
                              ? "border-emerald-500 bg-emerald-500/10"
                              : "border-slate-700"
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center w-4 h-4 rounded-full transition-colors duration-500 ${
                              revealed ? "bg-emerald-500 text-white" : "bg-slate-700 text-transparent"
                            }`}
                          >
                            <Icon name="check" className="w-3 h-3" />
                          </span>
                          <div className="h-2 w-24 bg-slate-600 rounded-full" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Skeleton fallback — demo.questions empty: never renders blank. */
                  <div className="p-8 space-y-4">
                    <Skeleton className="h-1.5 w-full rounded-full mb-6" />
                    <Skeleton className="h-3 w-3/4 rounded-full" />
                    <Skeleton className="h-3 w-1/2 rounded-full mb-8" />
                    {[0, 1, 2].map((o) => (
                      <Skeleton key={o} className="h-10 w-full rounded-xl" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoExamSection;
